import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Star, ArrowRight, CheckCircle, Clock, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import ReviewWizard from "../components/ReviewWizard";

export default function HomeownerRatings() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [pendingRatings, setPendingRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "homeowner") {
      navigate("/login");
      return;
    }
    fetchPendingRatings();
  }, [user, navigate]);

  const fetchPendingRatings = async () => {
    try {
      const response = await axios.get(`${API}/api/ratings/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingRatings(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleRateClick = (item) => {
    setSelectedItem(item);
    setReviewModalOpen(true);
  };

  const handleRatingSuccess = () => {
    fetchPendingRatings();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="spinner w-10 h-10 border-4 border-[#0FA3B1]/20 border-t-[#0FA3B1] rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1F2933] py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Rate Contractors</h1>
            <Button
              variant="outline"
              className="text-white border-white/30 hover:bg-white/10"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Intro Card */}
        <Card className="mb-8 border-[#0FA3B1]/20 bg-[#0FA3B1]/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              Your Feedback Matters
            </CardTitle>
            <CardDescription>
              Help us prioritize great pros by rating contractors you&apos;ve worked with.
              Takes less than 30 seconds!
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Pending Ratings List */}
        {pendingRatings.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-[#7ED957]" />
              <h3 className="text-xl font-bold text-[#1F2933] mb-2">All Caught Up!</h3>
              <p className="text-[#1F2933]/60 mb-6">
                You don&apos;t have any pending ratings. Once a contractor contacts you,
                you&apos;ll be able to rate them here.
              </p>
              <Button
                onClick={() => navigate("/request")}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98]"
              >
                Request a Service <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-[#1F2933]">
              Contractors to Rate ({pendingRatings.length})
            </h2>
            {pendingRatings.map((item) => (
              <Card key={`${item.lead_id}-${item.contractor_id}`} className="border-gray-100">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0FA3B1]/10 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-[#0FA3B1]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1F2933]">
                          {item.contractor_name}
                        </h3>
                        <p className="text-sm text-[#1F2933]/60">
                          {item.category_name}
                        </p>
                        <p className="text-xs text-[#1F2933]/40 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          Unlocked {new Date(item.unlocked_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRateClick(item)}
                      className="bg-[#0FA3B1] hover:bg-[#0D8C98]"
                      data-testid={`rate-btn-${item.contractor_id}`}
                    >
                      <Star className="w-4 h-4 mr-2" /> Rate Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Review Wizard Modal */}
      <ReviewWizard
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        lead={selectedItem}
        contractor={selectedItem}
        onSuccess={handleRatingSuccess}
      />
    </div>
  );
}
